import { useState, useEffect, useRef } from "react";

export function useTimeRemaining(endTime, onAbandon) {
	const [timeRemaining, setTimeRemaining] = useState("");
	const [isOverdue, setIsOverdue] = useState(false);
	const endTimeRef = useRef(endTime);
	const onAbandonRef = useRef(onAbandon);

	// Update refs when props change
	useEffect(() => {
		endTimeRef.current = endTime;
		onAbandonRef.current = onAbandon;
	}, [endTime, onAbandon]);

	useEffect(() => {
		if (!endTimeRef.current) {
			setTimeRemaining("00:00");
			return;
		}

		const calculateTimeRemaining = () => {
			const now = new Date();
			const endTime = new Date(endTimeRef.current);
			const diffMs = endTime - now;

			// If time is past -10 minutes, mark session as abandoned and show -10:00
			if (diffMs <= -600000) {
				// -10 minutes in milliseconds
				setTimeRemaining("-10:00");
				setIsOverdue(true);
				// Call abandon callback
				if (onAbandonRef.current) {
					onAbandonRef.current();
				}
				return false;
			}

			// Handle negative time (overdue) up to -10 minutes
			if (diffMs <= 0) {
				const absDiffMs = Math.abs(diffMs);
				const diffMins = Math.floor(absDiffMs / 60000);
				const diffSecs = Math.floor((absDiffMs % 60000) / 1000);

				// Format as -MM:SS
				const formattedMins = String(diffMins).padStart(2, "0");
				const formattedSecs = String(diffSecs).padStart(2, "0");

				setTimeRemaining(`-${formattedMins}:${formattedSecs}`);
				setIsOverdue(true);
				return true;
			}

			// Convert to minutes and seconds (positive time remaining)
			const diffMins = Math.floor(diffMs / 60000);
			const diffSecs = Math.floor((diffMs % 60000) / 1000);

			// Format as MM:SS
			const formattedMins = String(diffMins).padStart(2, "0");
			const formattedSecs = String(diffSecs).padStart(2, "0");

			setTimeRemaining(`${formattedMins}:${formattedSecs}`);
			setIsOverdue(false);
			return true;
		};

		// Calculate immediately
		calculateTimeRemaining();

		// Update every second
		const timer = setInterval(() => {
			const shouldContinue = calculateTimeRemaining();
			if (!shouldContinue) {
				clearInterval(timer);
			}
		}, 1000);

		return () => clearInterval(timer);
	}, [endTime]);

	return { timeRemaining, isOverdue };
}
